import { useState } from 'react'
import type { CalcResult, CandidatePosition, ProbabilityTag, SchoolProbability } from '@/domain/types'
import { formatProbPercent, probStrength, probToneClass, showProbPercent } from '@/lib/format'

const TAG_LABELS: Record<ProbabilityTag, string> = {
  safe: '稳',
  recommended: '可报',
  reach: '冲',
  risky: '风险',
  unlikely: '低概率'
}

const TAG_CLASSES: Record<ProbabilityTag, string> = {
  safe: 'tag-safe',
  recommended: 'tag-recommended',
  reach: 'tag-reach',
  risky: 'tag-risky',
  unlikely: 'tag-unlikely'
}

/**
 * 一中「积极劝冲 + 风险提示」建议条。
 * 仅对够得着 / 临界的考生显示（冲 / 可报 / 风险）；真够不着（低概率、
 * 名额已满 floored）不劝冲，避免误导。按 tag 给差异化力度，始终配套保底提示。
 */
const YIZHONG_RUSH: Partial<Record<ProbabilityTag, string>> = {
  reach: '够得着就大胆冲一中！它是邹城第一梯队，别因顺位犹豫；但滑档风险确实存在，务必用二中兜底，冲稳结合。',
  recommended: '一中很有希望，值得积极冲一冲；稳妥起见仍建议把二中放在保底位，避免段内顺位波动导致滑档。',
  risky: '一中希望偏小，但仍可作为冲刺志愿填报；关键是把二中 / 实验放在稳妥位置、做好保底。'
}

export function SchoolsSection({ result }: { result: CalcResult }) {
  const top300 = result.position.inTop300
  return (
    <section>
      <h2 className="section-title px-1">{top300 ? '录取结果' : '五校录取概率'}</h2>
      {top300 && (
        <p className="px-1 mb-3 text-ink-500 text-sm leading-relaxed">
          你已进入全市前 300 名。按教体局规则，前 300 分数线上的考生直接录取至邹城一中，不进入分段志愿录取流程，故无需测算其他学校。
        </p>
      )}
      <div className="space-y-3">
        {result.schools.map((s) => (
          <SchoolCard key={s.school} item={s} position={result.position} />
        ))}
      </div>
      <p className="px-1 mt-3 text-ink-400 text-xs leading-relaxed">
        录取前提：学业考试科目及综合素质评价等级须达到 C（含 C）级以上（教体局规则），本工具不校验该条件。
      </p>
    </section>
  )
}

function SchoolCard({ item, position }: { item: SchoolProbability; position: CandidatePosition }) {
  const [expanded, setExpanded] = useState(false)

  const strength = probStrength(item.point, item.lower, item.upper, item.structuralCertain)
  const rushTip = item.school === '邹城一中' ? YIZHONG_RUSH[item.tag] : undefined
  // 是否展示精确百分比：仅在「结果可靠」时（前 300 / 结构性确定 / 段内高位且达「稳」），
  // 其余（五五开、需保底等）只给可能性描述、不给数字，避免与经验分档（尾号表）观感偏差过大。
  const showPercent = showProbPercent(item.school, item.tag, item.structuralCertain, position)
  // 非结构性确定时，区间下界并入「保守情境（报该校人更多）」，让段内靠后 / 保底校
  // 「稳进依赖前方少人报该校」这一下行风险在区间里可见，而非退化成 100–100%；
  // 同时与上界一并封顶 99%，避免出现裸 100% 或「下界 100 > 上界 99」的倒挂。
  const lowerVal = item.structuralCertain
    ? item.lower
    : Math.min(item.lower, item.scenarios.pessimistic, 0.99)
  const lower = formatProbPercent(lowerVal)
  const upper = formatProbPercent(strength.certain ? item.upper : Math.min(item.upper, 0.99))
  // 「稳 / 可报」却非结构性确定、且保守情境有实质回落（<97%）→ 提示稳进前提
  const hedgePess =
    !item.structuralCertain &&
    (item.tag === 'safe' || item.tag === 'recommended') &&
    item.scenarios.pessimistic < 0.97
      ? Math.round(item.scenarios.pessimistic * 100)
      : null
  const isZero = item.point === 0
  const isFloored = item.floored === true
  const dimmed = isZero || isFloored

  return (
    <div className={`card transition-opacity ${dimmed ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="text-h3 text-ink-900">{item.school}</div>
          {item.narrative && !dimmed && (
            <div className="text-ink-600 text-sm mt-1 leading-relaxed">{item.narrative}</div>
          )}
          {isZero && (
            <div className="text-ink-500 text-sm mt-1 leading-relaxed">
              该分数段名额大概率在轮到你前已分配完毕
            </div>
          )}
          {isFloored && (
            <div className="text-ink-500 text-sm mt-1 leading-relaxed">
              该分数段名额大概率在轮到你前已分配完毕；下限概率基于当前预测人群的报考意愿热度动态校准，不代表绝对不可能
            </div>
          )}
        </div>
        <span className={'tag ' + TAG_CLASSES[item.tag]}>{TAG_LABELS[item.tag]}</span>
      </div>

      {showPercent ? (
        <>
          <div className="mt-4 flex items-end gap-3 flex-wrap">
            <div>
              <div className="text-ink-500 text-sm">
                录取概率
                <span className={'ml-1.5 font-semibold ' + probToneClass(strength.tone)}>
                  {strength.label}
                </span>
              </div>
              <div className="text-[36px] leading-none font-semibold text-ink-900 tabular-nums">
                {strength.display}
                {strength.tone !== 'none' && <span className="text-h2 text-ink-500 ml-1">%</span>}
              </div>
            </div>
            {strength.tone !== 'none' && (
              <div className="flex items-center gap-1.5 pb-1.5">
                <span className="px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 text-sm tabular-nums font-medium border border-brand-100">
                  {lower}% – {upper}%
                </span>
                <span className="text-ink-400 text-xs">概率区间</span>
              </div>
            )}
          </div>

          <ProbBar lower={lowerVal} upper={item.upper} point={item.point} />

          {hedgePess !== null && !dimmed && (
            <div className="mt-3 px-3 py-2 rounded-lg bg-sky-50 border border-sky-100 text-sky-900 text-sm leading-relaxed">
              <span className="font-semibold text-sky-800">稳进有前提 · </span>
              此结论假设前方较少高分考生把{item.school}填第一志愿；若该校报考转热，录取概率可能回落至约 {hedgePess}%。志愿按分段第一志愿分配，第一志愿报本校时建议同时关注往年录取线。
            </div>
          )}
        </>
      ) : (
        <div className="mt-4">
          <div className="text-ink-500 text-sm">录取可能性</div>
          <div className={'text-h2 font-semibold mt-0.5 ' + probToneClass(strength.tone)}>
            {strength.label}
          </div>
          {!dimmed && (
            <div className="text-ink-400 text-xs mt-1 leading-relaxed">
              该顺位录取变数较大，仅给可能性判断；建议拉开冲 / 稳 / 保梯度，别把志愿全押在此
            </div>
          )}
        </div>
      )}

      {rushTip && !dimmed && (
        <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-100 text-amber-900 text-sm leading-relaxed">
          <span className="font-semibold text-amber-800">冲一中建议 · </span>
          {rushTip}
        </div>
      )}

      <button
        onClick={() => setExpanded((v) => !v)}
        className="mt-4 text-brand-700 text-sm active:opacity-70"
      >
        {expanded ? '收起明细 ▴' : '查看计算明细 ▾'}
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-ink-100 space-y-4">
          <div>
            <div className="text-ink-500 text-sm mb-2">
              三种情境推演
              <span className="text-ink-400 text-xs ml-2">保守 / 乐观 = 报该校的人比预测多 / 少 20%</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <ScenarioCell label="保守" value={item.scenarios.pessimistic} color="bg-rose-50 text-rose-800" />
              <ScenarioCell label="中性" value={item.scenarios.neutral} color="bg-brand-50 text-brand-800" />
              <ScenarioCell label="乐观" value={item.scenarios.optimistic} color="bg-emerald-50 text-emerald-800" />
            </div>
          </div>

          <div>
            <div className="text-ink-500 text-sm mb-2">6 项专业指标</div>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <Metric
                label="满额预警线"
                value={`${(item.metrics.burnoutPoint * 100).toFixed(0)}%`}
                hint="名额耗尽时的累计位置阈值"
              />
              <Metric
                label="报名热度"
                value={`×${item.metrics.heatPremium.toFixed(2)}`}
                hint="预测报名 ÷ 官方比例"
              />
              <Metric
                label="捡漏指数"
                value={`×${item.metrics.bargainIndex.toFixed(2)}`}
                hint="官方比例 ÷ 预测报名"
              />
              <Metric
                label="段内压强"
                value={`${item.metrics.perScorePressure.toFixed(1)} 人/分`}
                hint="所在段平均每分人数"
              />
              <Metric
                label="顺位安全垫"
                value={
                  item.metrics.safetyMargin >= 0
                    ? `+${(item.metrics.safetyMargin * 100).toFixed(0)}%`
                    : `-${Math.abs(item.metrics.safetyMargin * 100).toFixed(0)}%`
                }
                hint="预警线减去你的位置"
                positive={item.metrics.safetyMargin > 0}
                negative={item.metrics.safetyMargin < 0}
              />
            </div>
          </div>

          <div>
            <div className="text-ink-500 text-sm mb-2">原始计算</div>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <Detail label="段内容量" value={item.capacity.toString()} />
              <Detail label="预测第一志愿人数" value={item.demand.toString()} />
              <Detail label="你前方预计已报" value={item.demandBefore.toString()} />
              <Detail label="轮到时剩余容量" value={item.remaining.toString()} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ScenarioCell({ label, value, color }: { label: string; value: number; color: string }) {
  return (
      <div className={'rounded-xl px-3 py-2 text-center ' + color}>
      <div className="text-xs opacity-70">{label}</div>
      <div className="text-h3 tabular-nums">{formatProbPercent(value)}%</div>
    </div>
  )
}

function Metric({
  label,
  value,
  hint,
  positive,
  negative
}: {
  label: string
  value: string
  hint?: string
  positive?: boolean
  negative?: boolean
}) {
  return (
    <div>
      <div className="text-ink-500 text-xs">{label}</div>
      <div
        className={
          'text-h3 tabular-nums ' +
          (positive ? 'text-emerald-700' : negative ? 'text-rose-700' : 'text-ink-900')
        }
      >
        {value}
      </div>
      {hint && <div className="text-ink-400 text-[11px] leading-tight mt-0.5">{hint}</div>}
    </div>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-ink-500">{label}：</span>
      <span className="text-ink-800 tabular-nums">{value}</span>
    </div>
  )
}

function ProbBar({ lower, upper, point }: { lower: number; upper: number; point: number }) {
  return (
    <div className="mt-3 relative h-4 rounded-full bg-ink-100 overflow-hidden">
      <div
        className="absolute h-full bg-brand-200 rounded-full"
        style={{ left: `${lower * 100}%`, width: `${Math.max(0, (upper - lower) * 100)}%` }}
      />
      <div
        className="absolute top-0.5 w-2 h-3 bg-brand-700 rounded-sm shadow-sm"
        style={{ left: `calc(${point * 100}% - 4px)` }}
      />
    </div>
  )
}
